// New events: <feature>_<action>_<target-of-action>
// lowercase, underscores between parts, dashes for spaces.
// Do not rename existing Title_Case or marketplace_* values — dashboards key off them.

import type { OuterFunnelStep, FunnelStep } from '@/app/quote-builder';
import type { CadLoadOutcome } from '@/app/quote-builder/cad-viewer/useCadEngine';
import { IntentAnswer } from '@/app/quote-builder/intent/IntentPanel';
import { Address, ConciergeMethod, MetaValues, OTPMethod, PaymentType, Visitor } from '@/models';

import { Channel } from './attribution';

export const TRACKING = {
  AD_LANDING: 'ad-campaign_landed',
  ADDRESS_FOCUS_INPUT: 'address_focus_input',
  AI_CHAT_CLICKED_ARTICLE: 'ai-chat_clicked_article',
  AI_CHAT_CLICKED_COMPARE_INSTALLERS: 'ai-chat_clicked_compare-installers',
  AI_CHAT_CLICKED_SUNNY_HEADER: 'ai-chat_clicked_sunny-header',
  AI_CHAT_CLICKED_SUNNY_HEADER_MENU: 'ai-chat_clicked_sunny-header-menu',
  AI_CHAT_CLICKED_SUNNY_STICKY_FOOTER: 'ai-chat_clicked_sunny-sticky-footer',
  AI_CHAT_CLICKED_TOOLTIP_FIELD: 'ai-chat_clicked_tooltip-field',
  AI_CHAT_CLICKED_VIEW_ESTIMATE: 'ai-chat_clicked_view-estimate',
  AI_CHAT_GENERATED_QUOTE: 'ai-chat_generated-quote',
  AI_CHAT_GENERATED_QUOTE_FROM_ADDRESS: 'ai-chat_generated-quote_from-address',
  AI_CHAT_GENERATED_QUOTE_FROM_BILL: 'ai-chat_generated-quote_from-bill',
  AI_CHAT_GENERATED_QUOTE_FROM_UTILITY_PROVIDER: 'ai-chat_generated-quote_from-utility-provider',
  AI_CHAT_SUBMIT_FOLLOWUP: 'ai-chat_submit_followup',
  AI_CHAT_SUBMIT_QUESTION: 'ai-chat_submit_question',
  AI_CHAT_SUBMIT_SUGGESTED_QUESTION: 'ai-chat_submit_suggested-question',
  BILL_PROJECTION_COMPLETED: 'bill-projection_viewed_results',
  BILL_PROJECTION_ESTIMATE_CTA_CLICKED: 'bill-projection_clicked_estimate-cta',
  BILL_PROJECTION_SCENARIO_SWITCHED: 'bill-projection_switched_scenario',
  BILL_PROJECTION_STARTED: 'bill-projection_entered_bill',
  BLACKOUT_BATTLE_ANSWERED_PROMPT: 'blackout-battle_answered_prompt',
  BLACKOUT_BATTLE_CLICKED_CTA: 'blackout-battle_clicked_cta',
  BLACKOUT_BATTLE_CLICKED_SHARE: 'blackout-battle_clicked_share',
  BLACKOUT_BATTLE_COMPLETED_GAME: 'blackout-battle_completed_game',
  BLACKOUT_BATTLE_STARTED_GAME: 'blackout-battle_started_game',
  BLOG_CTA_CLICK: 'Home_Solar_Blog_Cta_Click',
  BLOG_GET_QUOTE: 'Home_Solar_Blog_Get_Quote',
  BLOG_READ_MORE: 'Home_Solar_Blog_Read_More',
  BLOG_SUBSCRIBE: 'Home_Solar_Blog_Subscribe',
  CAD_EXPANDED: 'cad_expanded_roof',
  CAD_INTERACTED: 'cad_interacted_roof',
  CAD_LOADED: 'cad_loaded_roof',
  CALL_US_CLICK: 'Inbound_Phone_CTA',
  CONTACT_FORM_SUBMIT: 'Contact_Form_Submit',
  ELECTRIFICATION_TOGGLED_SOLUTION: 'electrification_toggled_solution',
  ELECTRIFICATION_UPDATED_INPUTS: 'electrification_updated_inputs',
  EMAIL_SUBMITTED_SOLAR_GUIDE: 'Email_Submitted_Solar_Guide',
  EMBED_CLOSED_MODAL: 'embed_closed_modal',
  EMBED_ENGAGED_MODAL: 'embed_engaged_modal',
  EMBED_OPENED_MODAL: 'embed_opened_modal',
  ENGAGEMENT_CONFIRMED: 'engagement_confirmed',
  FOCUS_ADDRESS_BAR: 'Focus_Address_Bar',
  HOME_PAGE_ENTERED_DOOR: 'home-page_entered_door',
  INSTALLER_CLICKED_VIEW_PRICING_CASH: 'installer_clicked_view-pricing-cash',
  INSTALLER_CLICKED_VIEW_PRICING_LOAN: 'installer_clicked_view-pricing-loan',
  INSTALLER_CLICKED_VIEW_PRICING_PPA: 'installer_clicked_view-pricing-ppa',
  INSTALLER_CONNECTED: 'Installer_Connected',
  INSTALLER_EXPAND_CONTACT: 'Contact_Installer_Button_Expand',
  INSTALLER_UPDATE_BATTERY: 'installer_updated_battery',
  INSTALLER_UPDATE_PANEL: 'installer_updated_panel',
  LANDING_CLICK_GET_QUOTE: 'landing_click_get-quote',
  LANDING_CLICK_TRUST_SIGNAL: 'landing_click_trust-signal',
  MARKETPLACE_CLICKED_BATTERY_ADDON_SUCCESS_CONTENT: 'marketplace_clicked_battery-addon-success-content',
  MARKETPLACE_CLICKED_CONTACT_PHONE: 'marketplace_borealis_clicked_contact-phone',
  MARKETPLACE_CLICKED_FAQ_BATTERY: 'marketplace_borealis_clicked_faq-battery',
  MARKETPLACE_CLICKED_FAQ_FIND_INSTALLER: 'marketplace_borealis_clicked_faq-find-installer',
  MARKETPLACE_CLICKED_FAQ_GOOD_CANDIDATE: 'marketplace_borealis_clicked_faq-good-candidate',
  MARKETPLACE_CLICKED_FAQ_HOW_LONG: 'marketplace_borealis_clicked_faq-how-long',
  MARKETPLACE_CLICKED_FAQ_MAINTENANCE: 'marketplace_borealis_clicked_faq-maintenance',
  MARKETPLACE_CLICKED_FAQ_NET_METERING: 'marketplace_borealis_clicked_faq-net-metering',
  MARKETPLACE_CLICKED_FAQ_PROPERTY_VALUE: 'marketplace_borealis_clicked_faq-property-value',
  MARKETPLACE_CLICKED_FAQ_SAVINGS: 'marketplace_borealis_clicked_faq-savings',
  MARKETPLACE_CLICKED_FAQ_TAX_INCENTIVES: 'marketplace_borealis_clicked_faq-tax-incentives',
  MARKETPLACE_CLICKED_FAQ_WARRANTY: 'marketplace_borealis_clicked_faq-warranty',
  MARKETPLACE_CLICKED_INTENT_CARD_ENERGY: 'marketplace_borealis_clicked_intent-card-energy',
  MARKETPLACE_CLICKED_INTENT_CARD_HOME_READY: 'marketplace_borealis_clicked_intent-card-home-ready',
  MARKETPLACE_CLICKED_INTENT_CARD_MAINTENANCE: 'marketplace_borealis_clicked_intent-card-maintenance',
  MARKETPLACE_CLICKED_INTENT_CARD_PROCESS: 'marketplace_borealis_clicked_intent-card-process',
  MARKETPLACE_CLICKED_INTENT_CARD_SAVINGS: 'marketplace_borealis_clicked_intent-card-savings',
  MARKETPLACE_CLICKED_INTENT_CARD_TAX: 'marketplace_borealis_clicked_intent-card-tax',
  MARKETPLACE_CLICKED_SYSTEM_MAINTENANCE_SUCCESS_CONTENT: 'marketplace_clicked_system-maintenance-success-content',
  MARKETPLACE_CLICKED_SYSTEM_MAINTENANCE_WAITLIST_CONTENT: 'marketplace_clicked_system-maintenance-waitlist-content',
  MARKETPLACE_FOCUS_CONTACT_FORM: 'marketplace_borealis_focus_contact-form',
  MARKETPLACE_FOCUS_ZIP_INPUT: 'marketplace_borealis_focus_zip-input',
  MARKETPLACE_INSTALLERPAGE_CLICKED_VIEW_INSTALLER_DETAILS: 'marketplace_installerPage_clicked_view-installer-details',
  MARKETPLACE_INSTALLERPAGE_CLICKED_VIEW_REVIEWS: 'marketplace_installerPage_clicked_view-reviews',
  MARKETPLACE_SUBMIT_CONTACT_FORM: 'marketplace_borealis_submit_contact-form',
  MARKETPLACE_VIEWED_BATTERY_ADDON_SUCCESS: 'marketplace_viewed_battery-addon-success',
  MARKETPLACE_VIEWED_SYSTEM_MAINTENANCE_SUCCESS: 'marketplace_viewed_system-maintenance-success',
  MARKETPLACE_VIEWED_SYSTEM_MAINTENANCE_WAITLIST: 'marketplace_viewed_system-maintenance-waitlist',
  NEWS_GET_QUOTE: 'Home_Solar_News_Get_Quote',
  NEWS_READ_MORE: 'Home_Solar_News_Read_More',
  OPEN_ENERGY_COST_DETAILS: 'Savings_Details_Expanded',
  OPEN_FINANCING_DETAILS: 'Financing_Details_Expanded',
  OPEN_INSTALLER_DETAILS: 'Installer_Details_Expanded',
  OPEN_SYSTEM_DETAILS: 'System_Details_Expanded',
  QUOTE_BUILDER_CLICKED_BACK: 'quote-builder_clicked_back',
  QUOTE_BUILDER_CLICKED_CONTINUE: 'quote-builder_clicked_continue',
  QUOTE_BUILDER_QUESTIONS_OPENED_INFO_MODAL: 'quote-builder_questions_opened_info-modal',
  QUOTE_BUILDER_QUESTIONS_REVISIT_ANSWER: 'quote-builder_questions_revisit-answer',
  QUOTE_BUILDER_SELECTED_INTENT: 'quote-builder_selected-intent',
  SOLAR_SURGE_CLICKED_CTA: 'solar-surge_clicked_cta',
  SOLAR_SURGE_CLICKED_SHARE: 'solar-surge_clicked_share',
  SOLAR_SURGE_COMPLETED_RUN: 'solar-surge_completed_run',
  SOLAR_SURGE_STARTED_GAME: 'solar-surge_started_game',
  STATE_CREDIT_INFO_MODAL_OPEN: 'State_Credit_Info_Modal_Open',
  STICKY_FOOTER_CALL: 'sticky-footer-call',
  SUBMIT_ANSWER_ADDRESS: 'Address',
  SUBMIT_ANSWER_BILL: 'Average_Bill',
  SUBMIT_ANSWER_ROOF_AGE: 'Roof_Age',
  SUBMIT_ANSWER_UTILITY_PROVIDER: 'Utility_Provider',
  SUBMIT_ANSWER_ZIP: 'Zip',
  SUBMIT_OTP: 'OTP_Submit',
  SUBMIT_ZIP_FROM_URL: 'Zip_From_URL',
  TRUST_SIGNAL_CLICKED_CTA: 'trust-signal_clicked_cta',
  UPDATE_BATTERIES: 'Battery_Storage',
  UPDATE_BILL: 'Slider_Adjustment',
  UPDATE_PANELS: 'Panels_Adjustment',
  VISITOR_LOADED: 'Visitor_Loaded',
} as const;

export type TrackingEvent = (typeof TRACKING)[keyof typeof TRACKING];

export const trackingToFunnelEvent: Partial<Record<TrackingEvent, TrackingEvent>> = {
  [TRACKING.AI_CHAT_GENERATED_QUOTE_FROM_ADDRESS]: TRACKING.AI_CHAT_GENERATED_QUOTE,
  [TRACKING.AI_CHAT_GENERATED_QUOTE_FROM_BILL]: TRACKING.AI_CHAT_GENERATED_QUOTE,
  [TRACKING.AI_CHAT_GENERATED_QUOTE_FROM_UTILITY_PROVIDER]: TRACKING.AI_CHAT_GENERATED_QUOTE,
  [TRACKING.EMAIL_SUBMITTED_SOLAR_GUIDE]: TRACKING.CONTACT_FORM_SUBMIT,
  [TRACKING.INSTALLER_UPDATE_BATTERY]: TRACKING.UPDATE_BATTERIES,
  [TRACKING.INSTALLER_UPDATE_PANEL]: TRACKING.UPDATE_PANELS,
  [TRACKING.MARKETPLACE_CLICKED_CONTACT_PHONE]: TRACKING.CALL_US_CLICK,
  [TRACKING.MARKETPLACE_SUBMIT_CONTACT_FORM]: TRACKING.CONTACT_FORM_SUBMIT,
  [TRACKING.STICKY_FOOTER_CALL]: TRACKING.CALL_US_CLICK,
  [TRACKING.SUBMIT_ZIP_FROM_URL]: TRACKING.SUBMIT_ANSWER_ZIP,
};

type GoogleAd = { googleActionId: number; googleAdLabel: string };

// TODO remove commented out events when we confirm we don't need them
export const AdEvents: Record<string, GoogleAd> = {
  [TRACKING.CALL_US_CLICK]: { googleActionId: 6919196814, googleAdLabel: '68cNCOSfxuYZEKiBh-Eo' },
  // [TRACKING.CTA_LEARN_MORE]: { googleActionId: 6919040321, googleAdLabel: 'mFNvCIKjxuYZEKiBh-Eo' },
  [TRACKING.INSTALLER_CONNECTED]: { googleActionId: 6874717653, googleAdLabel: '9qhcCIWjxuYZEKiBh-Eo' },
  [TRACKING.INSTALLER_EXPAND_CONTACT]: { googleActionId: 6919040321, googleAdLabel: 'mFNvCIKjxuYZEKiBh-Eo' },
  // [TRACKING.ORDER_CREATED]: { googleActionId: 6940305974, googleAdLabel: 'MUw1CLbUsu0ZEKiBh-Eo' },
  [TRACKING.SUBMIT_ANSWER_ADDRESS]: { googleActionId: 6919254199, googleAdLabel: 'lh7BCLO4xeYZEKiBh-Eo' },
  // [TRACKING.SUBMIT_PII_C_SMS]: { googleActionId: 6874717650, googleAdLabel: 't8yBCOefxuYZEKiBh-Eo' },
  // [TRACKING.SUBMIT_PII_C_VC]: { googleActionId: 6874717647, googleAdLabel: 'CTTACOqfxuYZEKiBh-Eo' },
  // [TRACKING.SUBMIT_PII_CI]: { googleActionId: 6874717653, googleAdLabel: '9qhcCIWjxuYZEKiBh-Eo' },
  // [TRACKING.SUBMIT_PII_HP]: { googleActionId: 6874717653, googleAdLabel: '9qhcCIWjxuYZEKiBh-Eo' },
};

// Significant funnel events forwarded server-side to the ad platforms (Meta + Google). Segment mappings decide which
// platform/event each becomes.
export const AD_EVENTS = new Set<TrackingEvent>([
  TRACKING.AD_LANDING,
  TRACKING.AI_CHAT_GENERATED_QUOTE,
  TRACKING.AI_CHAT_GENERATED_QUOTE_FROM_ADDRESS,
  TRACKING.AI_CHAT_GENERATED_QUOTE_FROM_BILL,
  TRACKING.AI_CHAT_GENERATED_QUOTE_FROM_UTILITY_PROVIDER,
  TRACKING.AI_CHAT_SUBMIT_FOLLOWUP,
  TRACKING.AI_CHAT_SUBMIT_QUESTION,
  TRACKING.BLACKOUT_BATTLE_STARTED_GAME,
  TRACKING.BLACKOUT_BATTLE_COMPLETED_GAME,
  TRACKING.BLOG_CTA_CLICK,
  TRACKING.BLOG_GET_QUOTE,
  TRACKING.BLOG_READ_MORE,
  TRACKING.BLOG_SUBSCRIBE,
  TRACKING.CALL_US_CLICK,
  TRACKING.CONTACT_FORM_SUBMIT,
  TRACKING.INSTALLER_CONNECTED,
  TRACKING.INSTALLER_EXPAND_CONTACT,
  TRACKING.LANDING_CLICK_GET_QUOTE,
  TRACKING.NEWS_GET_QUOTE,
  TRACKING.NEWS_READ_MORE,
  TRACKING.EMAIL_SUBMITTED_SOLAR_GUIDE,
  TRACKING.QUOTE_BUILDER_CLICKED_BACK,
  TRACKING.QUOTE_BUILDER_CLICKED_CONTINUE,
  TRACKING.SUBMIT_ANSWER_ADDRESS,
  TRACKING.SUBMIT_ANSWER_BILL,
  TRACKING.SUBMIT_ANSWER_UTILITY_PROVIDER,
  TRACKING.SUBMIT_ANSWER_ZIP,
  TRACKING.SUBMIT_OTP,
  // Solar Surge game events fire as normal product analytics but are deliberately NOT forwarded to
  // Google/Meta as conversions while the game is an experimental MVP; add them back if we want the
  // signal once it proves out.
  TRACKING.STICKY_FOOTER_CALL,
]);

export type EventData = {
  address?: Address;
  badge?: string;
  batteryCount?: number;
  bill?: number | string;
  channel?: Channel; // acquisition channel
  component?: string;
  concierge?: ConciergeMethod;
  coverage?: number; // The electricity offset
  email?: string;
  landing_page?: string;
  ad_event?: boolean; // marks events the backend forwards server-side to the ad platforms
  first_name?: string;
  installerId?: string;
  installerName?: string;
  intent_tiles_order?: string[];
  last_name?: string;
  message?: Visitor['message'];
  meta_values?: MetaValues; // TODO remove this once <VisitorProvider /> is in the root src/app/layout.tsx
  orderNum?: string;
  otp?: OTPMethod;
  page?: string;
  panelCount?: number;
  paymentType?: PaymentType;
  phone?: string;
  placement?: string; // distinguishes multiple placements of the same CTA event
  roof_age?: string;
  send_solar_guide?: boolean;
  state?: string; // two-letter state code
  sun?: string;
  utility_provider?: string;
  utmSource?: string;
  zip?: string;
};

// Passive engagement signal payload — written when a real human first interacts with a page.
// Not part of EventData: these events bypass trackEvent (no ad-platform fan-out).
export type EngagementSignalData = {
  has_been_active?: boolean;
  time_to_first_interaction_ms?: number;
};

type GeneratedQuotePayload = { address?: Address; bill?: number | string; utility_provider?: string; zip?: string };

export type EventPayloads = {
  [TRACKING.AD_LANDING]: { channel?: Channel; landing_page?: string };
  [TRACKING.AI_CHAT_CLICKED_ARTICLE]: { title: string; url: string };
  [TRACKING.AI_CHAT_CLICKED_TOOLTIP_FIELD]: { field: string };
  [TRACKING.AI_CHAT_GENERATED_QUOTE]: GeneratedQuotePayload;
  [TRACKING.AI_CHAT_GENERATED_QUOTE_FROM_ADDRESS]: GeneratedQuotePayload;
  [TRACKING.AI_CHAT_GENERATED_QUOTE_FROM_BILL]: GeneratedQuotePayload;
  [TRACKING.AI_CHAT_GENERATED_QUOTE_FROM_UTILITY_PROVIDER]: GeneratedQuotePayload;
  [TRACKING.AI_CHAT_SUBMIT_SUGGESTED_QUESTION]: { question: string };
  [TRACKING.BILL_PROJECTION_COMPLETED]: { monthlyBillUsd: number | null; monthlyKwh: number | null; state: string };
  [TRACKING.BILL_PROJECTION_ESTIMATE_CTA_CLICKED]: { page: string; state: string };
  [TRACKING.BILL_PROJECTION_SCENARIO_SWITCHED]: { scenario: string; state: string };
  [TRACKING.BILL_PROJECTION_STARTED]: { inputMode: string; state: string };
  [TRACKING.BLACKOUT_BATTLE_ANSWERED_PROMPT]: { choice: string; event: string };
  [TRACKING.BLACKOUT_BATTLE_CLICKED_CTA]: { page: string };
  [TRACKING.BLACKOUT_BATTLE_CLICKED_SHARE]: { rating: number; scenario: string };
  [TRACKING.BLACKOUT_BATTLE_COMPLETED_GAME]: { scenario: string; score: number; title: string };
  [TRACKING.BLACKOUT_BATTLE_STARTED_GAME]: { difficulty: string; scenario: string };
  [TRACKING.BLOG_CTA_CLICK]: { page: string; placement?: string; utmSource?: string };
  [TRACKING.BLOG_GET_QUOTE]: { page: string };
  [TRACKING.BLOG_READ_MORE]: { page: string };
  [TRACKING.CAD_INTERACTED]: { input: 'pointer' | 'wheel' };
  [TRACKING.CAD_LOADED]: { loadMs?: number; outcome: CadLoadOutcome; panelCount?: number };
  [TRACKING.CONTACT_FORM_SUBMIT]: { email: string; first_name: string; last_name: string };
  // TDOD remove meta_values once <VisitorProvider /> is in the root src/app/layout.tsx
  [TRACKING.CALL_US_CLICK]: { component?: string; conciergePhone: string; meta_values?: MetaValues; page: string };
  [TRACKING.ELECTRIFICATION_TOGGLED_SOLUTION]: { enabled: boolean; solution: string };
  [TRACKING.ELECTRIFICATION_UPDATED_INPUTS]: { bill: number; zip: string };
  [TRACKING.EMAIL_SUBMITTED_SOLAR_GUIDE]: { email: string; send_solar_guide: boolean };
  [TRACKING.HOME_PAGE_ENTERED_DOOR]: { door: string };
  [TRACKING.INSTALLER_CONNECTED]: {
    address: Address;
    batteryCount?: number;
    bill: number | string | null;
    email?: string;
    installerId: string;
    installerName: string;
    panelCount?: number;
    paymentType?: PaymentType;
    phone?: string;
    quoteSetId?: string;
  };
  [TRACKING.INSTALLER_EXPAND_CONTACT]: { installerId: string; installerName: string; placement?: string };
  [TRACKING.INSTALLER_UPDATE_BATTERY]: { batteryCount?: number; panelCount?: number; page: string };
  [TRACKING.INSTALLER_UPDATE_PANEL]: { batteryCount?: number; panelCount?: number };
  [TRACKING.LANDING_CLICK_GET_QUOTE]: { page: string };
  [TRACKING.MARKETPLACE_CLICKED_BATTERY_ADDON_SUCCESS_CONTENT]: { component: 'blog' | 'guide' | 'game' };
  [TRACKING.MARKETPLACE_CLICKED_INTENT_CARD_ENERGY]: { intent_tiles_order: string[] };
  [TRACKING.MARKETPLACE_CLICKED_INTENT_CARD_HOME_READY]: { intent_tiles_order: string[] };
  [TRACKING.MARKETPLACE_CLICKED_INTENT_CARD_MAINTENANCE]: { intent_tiles_order: string[] };
  [TRACKING.MARKETPLACE_CLICKED_INTENT_CARD_PROCESS]: { intent_tiles_order: string[] };
  [TRACKING.MARKETPLACE_CLICKED_INTENT_CARD_SAVINGS]: { intent_tiles_order: string[] };
  [TRACKING.MARKETPLACE_CLICKED_INTENT_CARD_TAX]: { intent_tiles_order: string[] };
  [TRACKING.MARKETPLACE_CLICKED_SYSTEM_MAINTENANCE_SUCCESS_CONTENT]: { component: 'blog' | 'guide' | 'game' };
  [TRACKING.MARKETPLACE_CLICKED_SYSTEM_MAINTENANCE_WAITLIST_CONTENT]: { component: 'blog' | 'guide' | 'game' };
  [TRACKING.MARKETPLACE_FOCUS_CONTACT_FORM]: { component: string };
  [TRACKING.MARKETPLACE_SUBMIT_CONTACT_FORM]: { message: Visitor['message'] };
  [TRACKING.NEWS_GET_QUOTE]: { page: string };
  [TRACKING.NEWS_READ_MORE]: { page: string };
  [TRACKING.OPEN_FINANCING_DETAILS]: { installerId: string; installerName: string };
  [TRACKING.OPEN_INSTALLER_DETAILS]: { installerId: string; installerName: string };
  [TRACKING.OPEN_ENERGY_COST_DETAILS]: { installerId: string; installerName: string };
  [TRACKING.OPEN_SYSTEM_DETAILS]: { installerId: string; installerName: string };
  [TRACKING.QUOTE_BUILDER_CLICKED_BACK]: { page: string };
  [TRACKING.QUOTE_BUILDER_CLICKED_CONTINUE]: { page: string };
  [TRACKING.QUOTE_BUILDER_QUESTIONS_OPENED_INFO_MODAL]: { panel: 'address' | 'bill' | 'utility' };
  [TRACKING.QUOTE_BUILDER_QUESTIONS_REVISIT_ANSWER]: {
    panel: 'address' | 'bill' | 'intent' | 'solar-guide' | 'utility';
  };
  [TRACKING.QUOTE_BUILDER_SELECTED_INTENT]: { intent: IntentAnswer; page: OuterFunnelStep.INTENT };
  [TRACKING.SOLAR_SURGE_CLICKED_CTA]: { page: string };
  [TRACKING.SOLAR_SURGE_CLICKED_SHARE]: { energyKwh: number; isHighScore: boolean };
  [TRACKING.SOLAR_SURGE_COMPLETED_RUN]: { energyKwh: number; isHighScore: boolean; topLevel: number };
  [TRACKING.STICKY_FOOTER_CALL]: { conciergePhone?: string };
  [TRACKING.SUBMIT_ANSWER_ADDRESS]: { address: Address; component?: string };
  [TRACKING.SUBMIT_ANSWER_BILL]: { bill: number | string };
  [TRACKING.SUBMIT_ANSWER_ROOF_AGE]: { roof_age: string };
  [TRACKING.SUBMIT_ANSWER_UTILITY_PROVIDER]: { page: FunnelStep; utility_provider: string };
  [TRACKING.SUBMIT_ANSWER_ZIP]: { zip: string };
  [TRACKING.SUBMIT_OTP]: { otp: OTPMethod };
  [TRACKING.SUBMIT_ZIP_FROM_URL]: { zip: string };
  [TRACKING.TRUST_SIGNAL_CLICKED_CTA]: { page: string };
  [TRACKING.UPDATE_BATTERIES]: { batteryCount: number; component: string; page: string };
  [TRACKING.UPDATE_BILL]: { bill: number | string; page: string };
  [TRACKING.UPDATE_PANELS]: { component: string; panelCount: number };
};

export type EventWithPayload = keyof EventPayloads;
export type EventNoPayload = Exclude<TrackingEvent, EventWithPayload>;

// Overloaded trackEvent
export interface TrackEventFn {
  <T extends EventNoPayload>(eventName: T): Promise<void>;
  <T extends EventWithPayload>(eventName: T, data: EventPayloads[T]): Promise<void>;
}
